/**
 * Email Service
 * 
 * Provides functionality to send emails, particularly for order notifications.
 * Uses the email server API to send emails via SendGrid.
 * 
 * @module EmailService
 */

import { ENV } from '../config/env';
import { PersonalizedDemoCredentials, buildPersonalizedDemoCredentials } from '../config/demoCredentials';
import { generateUsernameFromName } from '../utils/generateUsername';

export interface EmailData {
  to: string;
  subject: string;
  body: string;
  cc?: string;
  bcc?: string;
}

export class EmailService {
  private static emailServerUrl: string = '';
  private static initialized: boolean = false;

  /**
   * Initialize the email service
   * Sets up the email server URL from environment variables or uses Cloud Function
   */
  static initialize(): void {
    if (!this.initialized) {
      const customUrl = import.meta.env.VITE_EMAIL_SERVER_URL;

      if (customUrl) {
        this.emailServerUrl = customUrl;
      } else {
        // Always use the Cloud Function unless a local email server is explicitly configured.
        // Localhost:3003 is not started with `npm run dev`, so demo emails would silently fail.
        const projectId = ENV.FIREBASE_PROJECT_ID || 'erp-mock';
        this.emailServerUrl = `https://us-central1-${projectId}.cloudfunctions.net/sendOrderEmail`;
      }

      this.initialized = true;
      console.log('📧 EmailService initialized with server:', this.emailServerUrl);
    }
  }

  /**
   * Send email via the email server API
   * 
   * @param emailData - Email data
   * @param attachments - Optional attachments array
   * @param cc - Optional CC email address(es) - can be string or array
   * @param bcc - Optional BCC email address(es) - can be string or array
   * @returns Promise resolving to success result
   */
  static async sendEmail(
    emailData: EmailData,
    attachments?: Array<{ content: string; filename: string; type: string }>,
    cc?: string | string[],
    bcc?: string | string[]
  ): Promise<{ success: boolean; error?: string }> {
    try {
      this.initialize();
      
      const payload: any = {
        to: emailData.to,
        subject: emailData.subject,
        body: emailData.body,
        attachments: attachments || []
      };

      if (cc) {
        payload.cc = cc;
      }

      if (bcc) {
        payload.bcc = bcc;
      }
      
      // Determine the correct endpoint URL
      // If using Cloud Function, the URL already includes the function name
      // If using local server, append /api/send-email
      const endpointUrl = this.emailServerUrl.includes('cloudfunctions.net') 
        ? this.emailServerUrl 
        : `${this.emailServerUrl}/api/send-email`;
      
      console.log('📧 Sending email to:', endpointUrl);
      console.log('📧 Base URL:', this.emailServerUrl);
      
      const response = await fetch(endpointUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      // Handle unauthorized errors
      if (response.status === 401 || response.status === 403) {
        const errorText = await response.text().catch(() => 'Unknown error');
        console.error('Unauthorized response:', response.status, errorText);
        throw new Error('Unauthorized: The email service is not publicly accessible. Please check Firebase Function IAM permissions. Status: ' + response.status);
      }

      // Check if response is JSON
      const contentType = response.headers.get('content-type');
      let result;
      if (contentType && contentType.includes('application/json')) {
        result = await response.json();
      } else {
        const text = await response.text();
        console.error('Non-JSON response:', text);
        throw new Error(`Unexpected response format: ${response.status} ${response.statusText}. Response: ${text.substring(0, 200)}`);
      }

      if (!response.ok || !result.success) {
        throw new Error(result.error || `Failed to send email: ${response.status} ${response.statusText}`);
      }

      return { success: true };
    } catch (error: any) {
      console.error('Error sending email:', error);
      return {
        success: false,
        error: error.message || 'Failed to send email'
      };
    }
  }

  /**
   * Convert blob to base64 string
   */
  private static async blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = (reader.result as string).split(',')[1];
        resolve(base64String);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  /**
   * Send order confirmation email with PDF attachment
   * 
   * @param customerEmail - Customer email address
   * @param pdfBlob - PDF blob to attach
   * @param orderId - Order ID
   * @param orderData - Order data
   * @param t - Optional translation function
   * @param ccEmail - Optional CC email address (defaults to mckenzieportal@gmail.com)
   * @returns Promise resolving to success result
   */
  static async sendOrderConfirmation(
    customerEmail: string,
    pdfBlob: Blob,
    orderId: string,
    orderData: any,
    t?: (key: string, defaultValue?: string) => string,
    ccEmail?: string
  ): Promise<void> {
    try {
      this.initialize();

      // Convert PDF blob to base64
      const pdfBase64 = await this.blobToBase64(pdfBlob);

      // Format email body
      const emailBody = this.formatOrderConfirmationEmail(orderData, orderId, t);

      // Prepare PDF attachment
      const attachments = [{
        content: pdfBase64,
        filename: `Order_${orderId}.pdf`,
        type: 'application/pdf',
        disposition: 'attachment'
      }];

      // Get subject with translation support
      const subject = t 
        ? t('email.orderConfirmationSubject', `Order Confirmation - ${orderId}`)
        : `Order Confirmation - ${orderId}`;

      // Default CC to mckenzieportal@gmail.com if not provided
      const cc = ccEmail || 'mckenzieportal@gmail.com';

      // Send email with CC to mckenzieportal@gmail.com
      const result = await this.sendEmail(
        {
          to: customerEmail,
          subject: subject,
          body: emailBody
        },
        attachments,
        cc // CC to mckenzieportal@gmail.com
      );

      if (!result.success) {
        throw new Error(result.error || 'Failed to send order confirmation email');
      }

      console.log('✅ Order confirmation email sent successfully to', customerEmail, 'with CC to', cc);
    } catch (error: any) {
      console.error('Error sending order confirmation:', error);
      throw error;
    }
  }

  /**
   * Send order notification email to customer service
   * 
   * @param pdfBlob - PDF blob to attach
   * @param orderId - Order ID
   * @param orderData - Order data
   * @param t - Optional translation function
   * @param serviceEmail - Customer service email (defaults to mckenzieportal@gmail.com)
   * @returns Promise resolving to success result
   */
  static async sendOrderToCustomerService(
    pdfBlob: Blob,
    orderId: string,
    orderData: any,
    t?: (key: string, defaultValue?: string) => string,
    serviceEmail: string = 'mckenzieportal@gmail.com'
  ): Promise<void> {
    try {
      this.initialize();

      // Convert PDF blob to base64
      const pdfBase64 = await this.blobToBase64(pdfBlob);

      // Format email body for customer service
      const emailBody = this.formatOrderNotificationEmail(orderData, orderId, t);

      // Prepare PDF attachment
      const attachments = [{
        content: pdfBase64,
        filename: `Order_${orderId}.pdf`,
        type: 'application/pdf',
        disposition: 'attachment'
      }];

      // Get subject with translation support
      const subject = t 
        ? t('email.orderNotificationSubject', `New Order - ${orderId}`)
        : `New Order - ${orderId}`;

      // Send email to customer service
      const result = await this.sendEmail(
        {
          to: serviceEmail,
          subject: subject,
          body: emailBody
        },
        attachments
      );

      if (!result.success) {
        throw new Error(result.error || 'Failed to send order notification email');
      }

      console.log('✅ Order notification email sent successfully to', serviceEmail);
    } catch (error: any) {
      console.error('Error sending order notification:', error);
      throw error;
    }
  }

  /**
   * Send approved-order PDF pack to customer service.
   * Alias used by OrderManagementService after approval.
   */
  static async sendOrderApprovalEmailToCustomerService(
    pdfBlob: Blob,
    orderId: string,
    orderData: any,
    t?: (key: string, defaultValue?: string) => string
  ): Promise<void> {
    return this.sendOrderToCustomerService(pdfBlob, orderId, orderData, t);
  }

  /**
   * Send demo credentials to a user
   * 
   * @param toEmail - User email address
   * @param userName - User name
   * @param loginUrl - URL to the login page
   * @returns Promise resolving to success result
   */
  static async sendDemoCredentials(
    toEmail: string,
    userName: string,
    loginUrl: string,
    credentials?: PersonalizedDemoCredentials
  ): Promise<{ success: boolean; error?: string }> {
    try {
      this.initialize();
      const access = credentials || buildPersonalizedDemoCredentials(generateUsernameFromName(userName));

      const emailBody = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <style>
            body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; line-height: 1.6; color: #333; margin: 0; padding: 0; background-color: #f5f5f5; }
            .container { max-width: 600px; margin: 0 auto; padding: 20px; }
            .header { background: #1a428a; color: white; padding: 30px 20px; text-align: center; border-radius: 8px 8px 0 0; }
            .content { background: #ffffff; padding: 30px; border-radius: 0 0 8px 8px; box-shadow: 0 2px 10px rgba(0,0,0,0.1); }
            .credential-box { background: #f8fafc; padding: 20px; margin: 20px 0; border-radius: 8px; border: 1px solid #e2e8f0; }
            .btn { display: inline-block; background-color: #6bb05e; color: white; padding: 12px 25px; text-decoration: none; border-radius: 6px; font-weight: bold; margin-top: 20px; }
            .footer { text-align: center; margin-top: 25px; color: #888; font-size: 12px; }
          </style>
        </head>
        <body>
          <div class="container">
            <div class="header">
              <h1>Nexis ERP Demo Access</h1>
            </div>
            <div class="content">
              <p>Hi ${userName},</p>
              <p>Thank you for requesting a demo of the Nexis ERP platform. You get <strong>two logins</strong> so you can try both roles:</p>
              
              <div class="credential-box">
                <h3 style="margin-top:0; color: #1a428a;">1. Manager Access</h3>
                <p style="margin: 0 0 12px 0; color: #64748b; font-size: 14px;">Headquarters tools: dashboard, stores, shipments, users, and payroll.</p>
                <p><strong>Username:</strong> ${access.manager.username}</p>
                <p><strong>Email:</strong> ${access.manager.email}</p>
                <p><strong>Password:</strong> ${access.manager.password}</p>
              </div>

              <div class="credential-box">
                <h3 style="margin-top:0; color: #1a428a;">2. Sales Rep Access</h3>
                <p style="margin: 0 0 12px 0; color: #64748b; font-size: 14px;">Field tools: store visits, mobile order entry, and notes.</p>
                <p><strong>Username:</strong> ${access.salesRep.username}</p>
                <p><strong>Email:</strong> ${access.salesRep.email}</p>
                <p><strong>Password:</strong> ${access.salesRep.password}</p>
              </div>

              <p>You can access the portal by clicking the button below:</p>
              <div style="text-align: center;">
                <a href="${loginUrl}" class="btn">Login to Demo</a>
              </div>

              <p style="margin-top: 30px; font-size: 14px; color: #666;">
                <strong>Note:</strong> This is a secure sandbox environment. Data is for demonstration purposes only.
              </p>
            </div>
            <div class="footer">
              <p>© ${new Date().getFullYear()} Nexis ERP. All rights reserved.</p>
            </div>
          </div>
        </body>
        </html>
      `;

      return await this.sendEmail({
        to: toEmail,
        subject: 'Your Nexis ERP Demo Logins: Manager & Sales Rep',
        body: emailBody
      });
    } catch (error: any) {
      console.error('Error sending demo credentials:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Notify admin about a new demo request
   */
  static async sendDemoRequestNotification(leadData: {
    name: string;
    email: string;
    phone: string;
    managerUsername?: string;
    salesRepUsername?: string;
  }): Promise<{ success: boolean; error?: string }> {
    try {
      this.initialize();
      const adminEmail = 'devbob.co@gmail.com';
      const emailBody = `
        <h2>New Demo Request - Nexis ERP</h2>
        <p><strong>Name:</strong> ${leadData.name}</p>
        <p><strong>Email:</strong> ${leadData.email}</p>
        <p><strong>Phone:</strong> ${leadData.phone}</p>
        ${leadData.managerUsername ? `<p><strong>Manager username:</strong> ${leadData.managerUsername}</p>` : ''}
        ${leadData.salesRepUsername ? `<p><strong>Sales rep username:</strong> ${leadData.salesRepUsername}</p>` : ''}
        <p><strong>Time:</strong> ${new Date().toLocaleString()}</p>
      `;
      return await this.sendEmail({
        to: adminEmail,
        subject: `NEW DEMO LEAD: ${leadData.name}`,
        body: emailBody
      });
    } catch (error: any) {
      console.error('Error sending admin notification:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Notify admin about a new quote request
   */
  static async sendQuoteRequestNotification(leadData: { name: string; email: string; company: string; message: string }): Promise<{ success: boolean; error?: string }> {
    try {
      this.initialize();
      const adminEmail = 'devbob.co@gmail.com';
      const emailBody = `
        <h2>New Quote Request - Nexis ERP</h2>
        <p><strong>Name:</strong> ${leadData.name}</p>
        <p><strong>Email:</strong> ${leadData.email}</p>
        <p><strong>Company:</strong> ${leadData.company}</p>
        <p><strong>Message:</strong> ${leadData.message}</p>
        <p><strong>Time:</strong> ${new Date().toLocaleString()}</p>
      `;
      return await this.sendEmail({
        to: adminEmail,
        subject: `NEW QUOTE REQUEST: ${leadData.company}`,
        body: emailBody
      });
    } catch (error: any) {
      console.error('Error sending admin notification:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Format order notification email body for customer service
   */
  private static formatOrderNotificationEmail(
    orderData: any,
    orderId: string,
    t?: (key: string, defaultValue?: string) => string
  ): string {
    const translate = (key: string, defaultValue: string) => t ? t(key, defaultValue) : defaultValue;

    // Get store name from database (StoreName field)
    const storeName = orderData.storeName || orderData.StoreName || orderData.customerName || 'N/A';
    const poNumber = orderData.poNumber || orderData.purchaseOrder || 'N/A';
    const orderDate = orderData.orderDate 
      ? new Date(orderData.orderDate).toLocaleDateString() 
      : new Date().toLocaleDateString();
    
    // Format delivery date - handle both string and Date objects
    let deliveryDate = 'N/A';
    if (orderData.deliveryDate) {
      try {
        const date = typeof orderData.deliveryDate === 'string' 
          ? new Date(orderData.deliveryDate) 
          : orderData.deliveryDate;
        if (!isNaN(date.getTime())) {
          deliveryDate = date.toLocaleDateString();
        }
      } catch (e) {
        // If date parsing fails, keep as N/A
        deliveryDate = 'N/A';
      }
    }
    
    // Calculate totals from items arrays if not provided
    const allItems = [
      ...(orderData.heritageItems || []),
      ...(orderData.livingstonItems || []),
      ...(orderData.flowerItems || []),
      ...(orderData.vegetableItems || []),
      ...(orderData.supplyItems || [])
    ];
    
    const totalItems = orderData.totalItems !== undefined 
      ? orderData.totalItems 
      : allItems.reduce((sum, item) => sum + (item.quantity || 0), 0);
    
    const totalAmount = orderData.totalAmount !== undefined
      ? orderData.totalAmount
      : allItems.reduce((sum, item) => sum + ((item.price || 0) * (item.quantity || 0)), 0);
    
    const customerEmail = orderData.email || orderData.customerEmail || 'N/A';

    // Get customer signature if available
    const customerSignature = orderData.customerSignature || null;
    const signatureDate = orderData.signatureDate || new Date().toLocaleDateString();

    // Build signature section HTML if signature exists
    const signatureSection = customerSignature ? `
            <div class="signature-section">
              <h3>${translate('email.signatureSection', 'Customer Signature')}</h3>
              <div class="signature-container">
                <div class="signature-box">
                  <div class="signature-image-wrapper">
                    <img src="${customerSignature}" alt="Customer Signature" class="signature-image" />
                  </div>
                  <div class="signature-line"></div>
                  <div class="signature-details">
                    <p><strong>${translate('email.name', 'Name')}:</strong> ${storeName}</p>
                    <p><strong>${translate('email.date', 'Date')}:</strong> ${signatureDate}</p>
                  </div>
                </div>
              </div>
            </div>
    ` : '';

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <style>
          body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; line-height: 1.6; color: #333; margin: 0; padding: 0; background-color: #f5f5f5; }
          .container { max-width: 650px; margin: 0 auto; padding: 20px; }
          .header { background: linear-gradient(135deg, #2196F3 0%, #1565C0 100%); color: white; padding: 30px 20px; text-align: center; border-radius: 8px 8px 0 0; }
          .header h1 { margin: 0; font-size: 24px; font-weight: 600; }
          .header .logo { font-size: 14px; opacity: 0.9; margin-top: 8px; }
          .content { background: #ffffff; padding: 30px; border-radius: 0 0 8px 8px; box-shadow: 0 2px 10px rgba(0,0,0,0.1); }
          .order-info { background: linear-gradient(135deg, #f3f8ff 0%, #e8f4fd 100%); padding: 20px; margin: 20px 0; border-radius: 8px; border-left: 4px solid #2196F3; }
          .order-info h3 { margin-top: 0; color: #1565C0; font-size: 18px; font-weight: 600; margin-bottom: 15px; }
          .order-info p { margin: 8px 0; font-size: 14px; }
          .order-info strong { color: #333; }
          .signature-section { background: linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%); padding: 25px; margin: 25px 0; border-radius: 8px; border: 1px solid #e0e0e0; }
          .signature-section h3 { margin-top: 0; color: #1565C0; font-size: 16px; font-weight: 600; margin-bottom: 15px; text-align: center; }
          .signature-container { display: flex; justify-content: center; }
          .signature-box { background: white; padding: 20px; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.08); min-width: 280px; text-align: center; }
          .signature-image-wrapper { background: linear-gradient(135deg, #ffffff 0%, #fafafa 100%); padding: 15px; border-radius: 6px; margin-bottom: 10px; border: 1px dashed #ddd; }
          .signature-image { max-width: 250px; max-height: 100px; width: auto; height: auto; display: block; margin: 0 auto; filter: contrast(1.1); }
          .signature-line { border-bottom: 2px solid #2196F3; margin: 15px auto 12px; width: 80%; }
          .signature-details { font-size: 13px; color: #666; }
          .signature-details p { margin: 4px 0; }
          .footer { text-align: center; margin-top: 25px; padding-top: 20px; border-top: 1px solid #e0e0e0; }
          .footer p { color: #888; font-size: 12px; margin: 5px 0; }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="header">
            <h1>${translate('email.newOrderNotification', 'New Order Notification')}</h1>
            <p class="logo">ERP Portal</p>
          </div>
          <div class="content">
            <p>${translate('email.dearCustomerService', 'Dear Customer Service Team,')}</p>
            <p>${translate('email.newOrderReceived', 'A new order has been received and requires processing.')}</p>
            
            <div class="order-info">
              <h3>${translate('email.orderDetails', 'Order Details')}</h3>
              <p><strong>${translate('email.orderId', 'Order ID')}:</strong> ${orderId}</p>
              <p><strong>${translate('email.poNumber', 'PO Number')}:</strong> ${poNumber}</p>
              <p><strong>${translate('email.storeName', 'Store Name')}:</strong> ${storeName}</p>
              <p><strong>${translate('email.customerEmail', 'Customer Email')}:</strong> ${customerEmail}</p>
              <p><strong>${translate('email.orderDate', 'Order Date')}:</strong> ${orderDate}</p>
              <p><strong>${translate('email.deliveryDate', 'Delivery Date')}:</strong> ${deliveryDate}</p>
              <p><strong>${translate('email.totalItems', 'Total Items')}:</strong> ${totalItems}</p>
              <p><strong>${translate('email.totalAmount', 'Total Amount')}:</strong> $${totalAmount.toFixed(2)}</p>
            </div>

            ${signatureSection}

            <p style="margin-top: 20px;">📎 ${translate('email.pdfAttached', 'A detailed PDF of the order is attached to this email.')}</p>
            <p>${translate('email.pleaseProcess', 'Please process this order accordingly.')}</p>
            <p style="margin-top: 20px;"><strong>ERP Portal System</strong></p>
          </div>
          <div class="footer">
            <p>© ${new Date().getFullYear()} ERP Mock. All rights reserved.</p>
            <p>${translate('email.footer', 'This is an automated email. Please do not reply.')}</p>
          </div>
        </div>
      </body>
      </html>
    `;
  }

  /**
   * Format order confirmation email body
   */
  private static formatOrderConfirmationEmail(
    orderData: any,
    orderId: string,
    t?: (key: string, defaultValue?: string) => string
  ): string {
    const translate = (key: string, defaultValue: string) => t ? t(key, defaultValue) : defaultValue;

    // Get store name from database (StoreName field)
    const storeName = orderData.storeName || orderData.StoreName || orderData.customerName || translate('email.valuedCustomer', 'Valued Customer');
    const poNumber = orderData.poNumber || orderData.purchaseOrder || 'N/A';
    const orderDate = orderData.orderDate 
      ? new Date(orderData.orderDate).toLocaleDateString() 
      : new Date().toLocaleDateString();
    
    // Format delivery date - handle both string and Date objects
    let deliveryDate = 'N/A';
    if (orderData.deliveryDate) {
      try {
        const date = typeof orderData.deliveryDate === 'string' 
          ? new Date(orderData.deliveryDate) 
          : orderData.deliveryDate;
        if (!isNaN(date.getTime())) {
          deliveryDate = date.toLocaleDateString();
        }
      } catch (e) {
        // If date parsing fails, keep as N/A
        deliveryDate = 'N/A';
      }
    }
    
    // Calculate totals from items arrays if not provided
    const allItems = [
      ...(orderData.heritageItems || []),
      ...(orderData.livingstonItems || []),
      ...(orderData.flowerItems || []),
      ...(orderData.vegetableItems || []),
      ...(orderData.supplyItems || [])
    ];
    
    const totalItems = orderData.totalItems !== undefined 
      ? orderData.totalItems 
      : allItems.reduce((sum, item) => sum + (item.quantity || 0), 0);
    
    const totalAmount = orderData.totalAmount !== undefined
      ? orderData.totalAmount
      : allItems.reduce((sum, item) => sum + ((item.price || 0) * (item.quantity || 0)), 0);

    // Get customer signature if available
    const customerSignature = orderData.customerSignature || null;
    const signatureDate = orderData.signatureDate || new Date().toLocaleDateString();

    // Build greeting with actual store name from database
    const greeting = `Dear ${storeName}, Thank you for your order with McKenzie Seeds. We are pleased to confirm the details of your order below.`;

    // Build signature section HTML if signature exists
    const signatureSection = customerSignature ? `
            <div class="signature-section">
              <h3>${translate('email.signatureSection', 'Signature Section')}</h3>
              <div class="signature-container">
                <div class="signature-box">
                  <p class="signature-label">${translate('email.customerSignature', 'Customer Signature')}</p>
                  <div class="signature-image-wrapper">
                    <img src="${customerSignature}" alt="Customer Signature" class="signature-image" />
                  </div>
                  <div class="signature-line"></div>
                  <div class="signature-details">
                    <p><strong>${translate('email.name', 'Name')}:</strong> ${storeName}</p>
                    <p><strong>${translate('email.date', 'Date')}:</strong> ${signatureDate}</p>
                  </div>
                </div>
              </div>
            </div>
    ` : '';

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <style>
          body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; line-height: 1.6; color: #333; margin: 0; padding: 0; background-color: #f5f5f5; }
          .container { max-width: 650px; margin: 0 auto; padding: 20px; }
          .header { background: linear-gradient(135deg, #4CAF50 0%, #2E7D32 100%); color: white; padding: 30px 20px; text-align: center; border-radius: 8px 8px 0 0; }
          .header h1 { margin: 0; font-size: 28px; font-weight: 600; letter-spacing: 0.5px; }
          .header .logo { font-size: 14px; opacity: 0.9; margin-top: 8px; }
          .content { background: #ffffff; padding: 30px; border-radius: 0 0 8px 8px; box-shadow: 0 2px 10px rgba(0,0,0,0.1); }
          .greeting { font-size: 16px; color: #333; margin-bottom: 20px; line-height: 1.8; }
          .order-info { background: linear-gradient(135deg, #f8fdf8 0%, #f0f7f0 100%); padding: 20px; margin: 20px 0; border-radius: 8px; border-left: 4px solid #4CAF50; }
          .order-info h3 { margin-top: 0; color: #2E7D32; font-size: 18px; font-weight: 600; margin-bottom: 15px; }
          .order-info p { margin: 8px 0; font-size: 14px; }
          .order-info strong { color: #333; }
          .signature-section { background: linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%); padding: 25px; margin: 25px 0; border-radius: 8px; border: 1px solid #e0e0e0; }
          .signature-section h3 { margin-top: 0; color: #2E7D32; font-size: 18px; font-weight: 600; margin-bottom: 20px; text-align: center; }
          .signature-container { display: flex; justify-content: center; }
          .signature-box { background: white; padding: 20px; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.08); min-width: 280px; text-align: center; }
          .signature-label { font-size: 14px; font-weight: 600; color: #4CAF50; margin-bottom: 12px; text-transform: uppercase; letter-spacing: 0.5px; }
          .signature-image-wrapper { background: linear-gradient(135deg, #ffffff 0%, #fafafa 100%); padding: 15px; border-radius: 6px; margin-bottom: 10px; border: 1px dashed #ddd; }
          .signature-image { max-width: 250px; max-height: 100px; width: auto; height: auto; display: block; margin: 0 auto; filter: contrast(1.1); }
          .signature-line { border-bottom: 2px solid #4CAF50; margin: 15px auto 12px; width: 80%; }
          .signature-details { font-size: 13px; color: #666; }
          .signature-details p { margin: 4px 0; }
          .pdf-notice { background: #e8f5e9; padding: 15px 20px; border-radius: 6px; margin: 20px 0; font-size: 14px; color: #2E7D32; border: 1px solid #c8e6c9; }
          .contact-section { background: #f5f5f5; padding: 20px; margin: 20px 0; border-radius: 8px; text-align: center; }
          .contact-section h3 { color: #2E7D32; margin-top: 0; font-size: 16px; }
          .contact-section p { margin: 5px 0; font-size: 13px; color: #666; }
          .footer { text-align: center; margin-top: 25px; padding-top: 20px; border-top: 1px solid #e0e0e0; }
          .footer p { color: #888; font-size: 12px; margin: 5px 0; }
          .closing { margin-top: 25px; font-size: 15px; }
          .closing p { margin: 5px 0; }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="header">
            <h1>${translate('email.orderConfirmation', 'Order Confirmation')}</h1>
            <p class="logo">McKenzie Seeds</p>
          </div>
          <div class="content">
            <p class="greeting">${greeting}</p>
            
            <div class="order-info">
              <h3>${translate('email.orderDetails', 'Order Details')}</h3>
              <p><strong>${translate('email.orderId', 'Order ID')}:</strong> ${orderId}</p>
              <p><strong>${translate('email.poNumber', 'PO Number')}:</strong> ${poNumber}</p>
              <p><strong>${translate('email.storeName', 'Store Name')}:</strong> ${storeName}</p>
              <p><strong>${translate('email.orderDate', 'Order Date')}:</strong> ${orderDate}</p>
              <p><strong>${translate('email.deliveryDate', 'Delivery Date')}:</strong> ${deliveryDate}</p>
              <p><strong>${translate('email.totalItems', 'Total Items')}:</strong> ${totalItems}</p>
              <p><strong>${translate('email.totalAmount', 'Total Amount')}:</strong> $${totalAmount.toFixed(2)}</p>
            </div>

            ${signatureSection}

            <div class="pdf-notice">
              📎 ${translate('email.pdfAttachment', 'Please find your order confirmation PDF attached to this email. The PDF contains complete details of your order including all product selections, quantities, and pricing.')}
            </div>

            <div class="contact-section">
              <h3>${translate('email.mckenzieSeedsContact', 'McKenzie Seeds Contact Information')}</h3>
              <p>📧 ${translate('email.contactEmail', 'mckenzieportal@gmail.com')}</p>
              <p>📞 ${translate('email.contactPhone', '1-800-665-6340 (Eng/Fr)')}</p>
              <p>📠 ${translate('email.contactFax', '1-888-883-8222')}</p>
              <p>📍 ${translate('email.contactAddress', '1000 Parker Blvd, Brandon, MB R7A 6E1')}</p>
            </div>

            <p style="font-size: 14px; color: #666; margin-top: 20px;">
              ${translate('email.questionsContact', 'If you have any questions or concerns about your order, please don\'t hesitate to contact us using the information above.')}
            </p>

            <div class="closing">
              <p>${translate('email.bestRegards', 'Best regards,')}</p>
              <p><strong>McKenzie Seeds Team</strong></p>
            </div>
          </div>
          <div class="footer">
            <p>© ${new Date().getFullYear()} McKenzie Seeds. All rights reserved.</p>
            <p>${translate('email.footer', 'This is an automated email. Please do not reply.')}</p>
          </div>
        </div>
      </body>
      </html>
    `;
  }

  /**
   * Send order approval notification email to customer service
   * Includes PDF and CSV/Spreadsheet attachments with customer info and order recap
   * 
   * @param pdfBlob - PDF blob to attach
   * @param csvBlob - CSV blob to attach (spreadsheet)
   * @param orderId - Order ID
   * @param orderData - Order data containing customer info
   * @param t - Optional translation function
   * @param serviceEmail - Customer service email (defaults to mckenzieportal@gmail.com)
   * @returns Promise resolving to success result
   */
  static async sendOrderApprovalNotification(
    pdfBlob: Blob,
    csvBlob: Blob,
    orderId: string,
    orderData: any,
    t?: (key: string, defaultValue?: string) => string,
    serviceEmail: string = 'mckenzieportal@gmail.com'
  ): Promise<void> {
    try {
      this.initialize();

      // Convert blobs to base64
      const pdfBase64 = await this.blobToBase64(pdfBlob);
      const csvBase64 = await this.blobToBase64(csvBlob);

      // Format email body with customer info and order recap
      const emailBody = this.formatOrderApprovalEmail(orderData, orderId, t);

      // Prepare attachments (PDF and CSV)
      const attachments = [
        {
          content: pdfBase64,
          filename: `Order_${orderId}.pdf`,
          type: 'application/pdf',
          disposition: 'attachment'
        },
        {
          content: csvBase64,
          filename: `Order_${orderId}.csv`,
          type: 'text/csv',
          disposition: 'attachment'
        }
      ];

      // Get subject with order details
      const storeName = orderData.storeName || orderData.StoreName || 'N/A';
      const poNumber = orderData.poNumber || orderId;
      const subject = t 
        ? t('email.orderApprovedSubject', `Order Approved - ${poNumber} - ${storeName}`)
        : `Order Approved - ${poNumber} - ${storeName}`;

      // Send email to customer service
      const result = await this.sendEmail(
        {
          to: serviceEmail,
          subject: subject,
          body: emailBody
        },
        attachments
      );

      if (!result.success) {
        throw new Error(result.error || 'Failed to send order approval notification email');
      }

      console.log('✅ Order approval notification email sent successfully to', serviceEmail);
    } catch (error: any) {
      console.error('Error sending order approval notification:', error);
      throw error;
    }
  }

  /**
   * Format order approval email body with customer info and order recap
   */
  private static formatOrderApprovalEmail(
    orderData: any,
    orderId: string,
    t?: (key: string, defaultValue?: string) => string
  ): string {
    const translate = (key: string, defaultValue: string) => t ? t(key, defaultValue) : defaultValue;

    // Get store/customer name from database
    const storeName = orderData.storeName || orderData.StoreName || orderData.customerName || 'N/A';
    const customerNumber = orderData.customerNumber || orderData.storeId || 'N/A';
    const poNumber = orderData.poNumber || orderData.purchaseOrder || orderId;
    const orderDate = orderData.orderDate 
      ? new Date(orderData.orderDate).toLocaleDateString() 
      : new Date().toLocaleDateString();
    
    // Format delivery date
    let deliveryDate = 'N/A';
    if (orderData.deliveryDate) {
      try {
        const date = typeof orderData.deliveryDate === 'string' 
          ? new Date(orderData.deliveryDate) 
          : orderData.deliveryDate;
        if (!isNaN(date.getTime())) {
          deliveryDate = date.toLocaleDateString();
        }
      } catch (e) {
        deliveryDate = 'N/A';
      }
    }
    
    // Customer contact info
    const address = orderData.address || 'N/A';
    const city = orderData.city || 'N/A';
    const province = orderData.province || orderData.state || 'N/A';
    const postalCode = orderData.postalCode || orderData.pc || 'N/A';
    const phone = orderData.phoneNumber || orderData.phone || 'N/A';
    const email = orderData.email || orderData.customerEmail || 'N/A';
    const salesRep = orderData.salesRepName || orderData.salesGroup || 'N/A';
    const orderType = orderData.orderType === 'custOrder' ? 'Customer Order' : 
                      orderData.orderType === 'offCarDelivery' ? 'Off Car Delivery' : 
                      'Standard Order';
    const specialInstructions = orderData.specialInstructions || 'None';

    // Calculate totals from items arrays
    const allItems = [
      ...(orderData.heritageItems || []),
      ...(orderData.livingstonItems || []),
      ...(orderData.flowerItems || []),
      ...(orderData.vegetableItems || []),
      ...(orderData.supplyItems || [])
    ];
    
    const totalItems = orderData.totalItems !== undefined 
      ? orderData.totalItems 
      : allItems.reduce((sum, item) => sum + (item.quantity || 0), 0);
    
    const totalAmount = orderData.totalAmount !== undefined
      ? orderData.totalAmount
      : allItems.reduce((sum, item) => sum + ((item.price || 0) * (item.quantity || 0)), 0);

    // Build items recap table
    const itemsRecap = allItems.length > 0 ? `
      <table style="width: 100%; border-collapse: collapse; margin: 15px 0; font-size: 13px;">
        <thead>
          <tr style="background: #f5f5f5;">
            <th style="padding: 10px; border: 1px solid #ddd; text-align: left;">Item Code</th>
            <th style="padding: 10px; border: 1px solid #ddd; text-align: left;">Description</th>
            <th style="padding: 10px; border: 1px solid #ddd; text-align: center;">Qty</th>
            <th style="padding: 10px; border: 1px solid #ddd; text-align: right;">Price</th>
            <th style="padding: 10px; border: 1px solid #ddd; text-align: right;">Total</th>
          </tr>
        </thead>
        <tbody>
          ${allItems.map(item => `
            <tr>
              <td style="padding: 8px; border: 1px solid #ddd;">${item.code || item.ItemCode || 'N/A'}</td>
              <td style="padding: 8px; border: 1px solid #ddd;">${item.packetDescription || item.name || 'N/A'}</td>
              <td style="padding: 8px; border: 1px solid #ddd; text-align: center;">${item.quantity || 0}</td>
              <td style="padding: 8px; border: 1px solid #ddd; text-align: right;">$${(item.price || 0).toFixed(2)}</td>
              <td style="padding: 8px; border: 1px solid #ddd; text-align: right;">$${((item.price || 0) * (item.quantity || 0)).toFixed(2)}</td>
            </tr>
          `).join('')}
        </tbody>
        <tfoot>
          <tr style="background: #e8f5e9; font-weight: bold;">
            <td colspan="2" style="padding: 10px; border: 1px solid #ddd;">Total</td>
            <td style="padding: 10px; border: 1px solid #ddd; text-align: center;">${totalItems}</td>
            <td style="padding: 10px; border: 1px solid #ddd;"></td>
            <td style="padding: 10px; border: 1px solid #ddd; text-align: right;">$${totalAmount.toFixed(2)}</td>
          </tr>
        </tfoot>
      </table>
    ` : '<p style="color: #666; font-style: italic;">No items in this order.</p>';

    // Get signature section if available
    const customerSignature = orderData.customerSignature || null;
    const signatureDate = orderData.signatureDate || new Date().toLocaleDateString();
    const signatureSection = customerSignature ? `
      <div style="background: #fafafa; padding: 20px; margin: 20px 0; border-radius: 8px; border: 1px solid #e0e0e0;">
        <h3 style="margin: 0 0 15px 0; color: #4CAF50; font-size: 16px;">Customer Signature</h3>
        <div style="background: white; padding: 15px; border-radius: 6px; text-align: center; border: 1px dashed #ddd;">
          <img src="${customerSignature}" alt="Customer Signature" style="max-width: 250px; max-height: 100px;" />
        </div>
        <div style="margin-top: 10px; font-size: 13px; color: #666; text-align: center;">
          <p style="margin: 4px 0;"><strong>Signed by:</strong> ${storeName}</p>
          <p style="margin: 4px 0;"><strong>Date:</strong> ${signatureDate}</p>
        </div>
      </div>
    ` : '';

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
      </head>
      <body style="font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; line-height: 1.6; color: #333; margin: 0; padding: 0; background-color: #f5f5f5;">
        <div style="max-width: 700px; margin: 0 auto; padding: 20px;">
          <!-- Header -->
          <div style="background: linear-gradient(135deg, #4CAF50 0%, #2E7D32 100%); color: white; padding: 30px 20px; text-align: center; border-radius: 8px 8px 0 0;">
            <h1 style="margin: 0; font-size: 24px; font-weight: 600;">✅ Order Approved</h1>
            <p style="font-size: 14px; opacity: 0.9; margin-top: 8px;">McKenzie Portal - Order Processing</p>
          </div>
          
          <!-- Content -->
          <div style="background: #ffffff; padding: 30px; border-radius: 0 0 8px 8px; box-shadow: 0 2px 10px rgba(0,0,0,0.1);">
            <p style="font-size: 16px; color: #333; margin-bottom: 20px;">
              An order has been <strong style="color: #4CAF50;">approved</strong> and is ready for processing. Please find the order details below along with the PDF and spreadsheet attachments.
            </p>
            
            <!-- Customer Information -->
            <div style="background: linear-gradient(135deg, #f8fdf8 0%, #f0f7f0 100%); padding: 20px; margin: 20px 0; border-radius: 8px; border-left: 4px solid #4CAF50;">
              <h3 style="margin: 0 0 15px 0; color: #2E7D32; font-size: 18px;">Customer Information</h3>
              <table style="width: 100%; font-size: 14px;">
                <tr>
                  <td style="padding: 5px 10px 5px 0; width: 40%;"><strong>Customer #:</strong></td>
                  <td style="padding: 5px 0;">${customerNumber}</td>
                </tr>
                <tr>
                  <td style="padding: 5px 10px 5px 0;"><strong>Store Name:</strong></td>
                  <td style="padding: 5px 0;">${storeName}</td>
                </tr>
                <tr>
                  <td style="padding: 5px 10px 5px 0;"><strong>Address:</strong></td>
                  <td style="padding: 5px 0;">${address}</td>
                </tr>
                <tr>
                  <td style="padding: 5px 10px 5px 0;"><strong>City/Province/Postal:</strong></td>
                  <td style="padding: 5px 0;">${city}, ${province} ${postalCode}</td>
                </tr>
                <tr>
                  <td style="padding: 5px 10px 5px 0;"><strong>Phone:</strong></td>
                  <td style="padding: 5px 0;">${phone}</td>
                </tr>
                <tr>
                  <td style="padding: 5px 10px 5px 0;"><strong>Email:</strong></td>
                  <td style="padding: 5px 0;">${email}</td>
                </tr>
                <tr>
                  <td style="padding: 5px 10px 5px 0;"><strong>Sales Representative:</strong></td>
                  <td style="padding: 5px 0;">${salesRep}</td>
                </tr>
              </table>
            </div>
            
            <!-- Order Information -->
            <div style="background: linear-gradient(135deg, #f3f8ff 0%, #e8f4fd 100%); padding: 20px; margin: 20px 0; border-radius: 8px; border-left: 4px solid #2196F3;">
              <h3 style="margin: 0 0 15px 0; color: #1565C0; font-size: 18px;">Order Information</h3>
              <table style="width: 100%; font-size: 14px;">
                <tr>
                  <td style="padding: 5px 10px 5px 0; width: 40%;"><strong>Order ID:</strong></td>
                  <td style="padding: 5px 0;">${orderId}</td>
                </tr>
                <tr>
                  <td style="padding: 5px 10px 5px 0;"><strong>PO Number:</strong></td>
                  <td style="padding: 5px 0;">${poNumber}</td>
                </tr>
                <tr>
                  <td style="padding: 5px 10px 5px 0;"><strong>Order Date:</strong></td>
                  <td style="padding: 5px 0;">${orderDate}</td>
                </tr>
                <tr>
                  <td style="padding: 5px 10px 5px 0;"><strong>Delivery Date:</strong></td>
                  <td style="padding: 5px 0;">${deliveryDate}</td>
                </tr>
                <tr>
                  <td style="padding: 5px 10px 5px 0;"><strong>Order Type:</strong></td>
                  <td style="padding: 5px 0;">${orderType}</td>
                </tr>
                <tr>
                  <td style="padding: 5px 10px 5px 0;"><strong>Special Instructions:</strong></td>
                  <td style="padding: 5px 0;">${specialInstructions}</td>
                </tr>
              </table>
            </div>
            
            <!-- Order Items Recap -->
            <div style="margin: 25px 0;">
              <h3 style="margin: 0 0 15px 0; color: #333; font-size: 18px;">📦 Order Items Recap</h3>
              ${itemsRecap}
            </div>

            ${signatureSection}
            
            <!-- Attachments Notice -->
            <div style="background: #e8f5e9; padding: 15px 20px; border-radius: 6px; margin: 20px 0; font-size: 14px; color: #2E7D32; border: 1px solid #c8e6c9;">
              <strong>📎 Attachments:</strong>
              <ul style="margin: 10px 0 0 0; padding-left: 20px;">
                <li>Order_${orderId}.pdf - Complete order document</li>
                <li>Order_${orderId}.csv - Spreadsheet for data processing</li>
              </ul>
            </div>
            
            <p style="margin-top: 25px; font-size: 14px;">Please process this order accordingly.</p>
            <p style="margin-top: 15px;"><strong>McKenzie Portal System</strong></p>
          </div>
          
          <!-- Footer -->
          <div style="text-align: center; margin-top: 25px; padding-top: 20px; border-top: 1px solid #e0e0e0;">
            <p style="color: #888; font-size: 12px; margin: 5px 0;">© ${new Date().getFullYear()} McKenzie Seeds. All rights reserved.</p>
            <p style="color: #888; font-size: 12px; margin: 5px 0;">${translate('email.footer', 'This is an automated email. Please do not reply.')}</p>
          </div>
        </div>
      </body>
      </html>
    `;
  }

  /**
   * Send an email using mailto link (fallback method)
   * This opens the user's default email client
   * 
   * @param emailData - Email data
   */
  static sendEmailViaMailto(emailData: EmailData): void {
    const { to, subject, body, cc, bcc } = emailData;
    
    let mailtoLink = `mailto:${encodeURIComponent(to)}?`;
    mailtoLink += `subject=${encodeURIComponent(subject)}&`;
    mailtoLink += `body=${encodeURIComponent(body)}`;
    
    if (cc) {
      mailtoLink += `&cc=${encodeURIComponent(cc)}`;
    }
    if (bcc) {
      mailtoLink += `&bcc=${encodeURIComponent(bcc)}`;
    }
    
    window.location.href = mailtoLink;
  }

}
